/**
 * FileAttachmentCard — Anthropic Claude.ai-style attachment chip.
 *
 * Two modes:
 *   - ``preview``: rendered in ChatInput before send. Shows status
 *     (valid / invalid / pending) and a remove button.
 *   - ``attached``: rendered inside a message bubble after the message has
 *     been sent. Clickable to open the file preview modal.
 *
 * Design rules (matching Claude.ai):
 *   - NO emojis. Document types are rendered as colored SVG icons
 *     (PDF red, DOCX blue, plain-text gray, generic neutral).
 *   - Images render their actual thumbnail (not an icon). The thumbnail
 *     is the focal point of the card.
 *   - Cards have rounded corners (12 px), tonal background, no harsh
 *     borders. They hover with a soft elevation change.
 *   - Layout uses design tokens — no raw hex / px literals.
 *   - RTL: every margin/padding uses logical properties so Hebrew users
 *     see the icon on the leading edge automatically.
 */

import { useEffect, useMemo, useState } from 'react';
import { theme } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { bidiIsolate } from '@/utils/bidi';
import api from '@/services/api';
import type { AttachmentSource } from '@/types/models';
import { ExternalLinkIcon, GoogleDriveGlyph } from './drive/driveIcons';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import './FileAttachmentCard.css';

/* ------------------------------------------------------------------------ */
/* Helpers                                                                  */
/* ------------------------------------------------------------------------ */

type FileKind = 'image' | 'pdf' | 'word' | 'spreadsheet' | 'presentation' | 'text' | 'font' | 'audio' | 'video' | 'other';

function getFileKind(name: string, mime?: string): FileKind {
  // Google-native files picked from Drive carry Google's mime types and no
  // extension; they arrive server-side as Word / Excel / PowerPoint.
  if (mime === 'application/vnd.google-apps.document') return 'word';
  if (mime === 'application/vnd.google-apps.spreadsheet') return 'spreadsheet';
  if (mime === 'application/vnd.google-apps.presentation') return 'presentation';
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (mime?.startsWith('image/')) return 'image';
  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (['doc', 'docx', 'pages'].includes(ext)) return 'word';
  if (['xlsx', 'xlsm', 'numbers'].includes(ext)) return 'spreadsheet';
  if (['pptx', 'key'].includes(ext)) return 'presentation';
  if (['ttf', 'otf', 'woff', 'woff2'].includes(ext)) return 'font';
  if (['txt', 'md', 'json', 'xml', 'log'].includes(ext)) return 'text';
  if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (['mp4', 'mov', 'avi', 'webm', 'mpg', 'mpeg'].includes(ext)) return 'video';
  return 'other';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Apple's three name their own format: a .numbers is not an Excel file,
 *  even though it opens in the same tools. */
const IWORK_LABELS: Record<string, string> = { numbers: 'NUMBERS', pages: 'PAGES', key: 'KEYNOTE' };

function getKindLabel(kind: FileKind, name = ''): string {
  const iwork = IWORK_LABELS[(name.split('.').pop() || '').toLowerCase()];
  if (iwork) return iwork;
  switch (kind) {
    case 'image': return 'IMAGE';
    case 'pdf': return 'PDF';
    case 'word': return 'WORD';
    case 'spreadsheet': return 'EXCEL';
    case 'presentation': return 'POWERPOINT';
    case 'text': return 'TEXT';
    case 'audio': return 'AUDIO';
    case 'video': return 'VIDEO';
    case 'font': return 'FONT';
    case 'other': return 'FILE';
  }
}

/** Maps a file kind to a brand-aware accent color. We use Ant Design's
 *  token palette so dark/light themes auto-flip. The PDF/Word/Text colors
 *  are the conventional Office-suite swatches. */
function useKindAccent(kind: FileKind) {
  const { token } = theme.useToken();
  switch (kind) {
    case 'pdf':
      return { bg: token.colorErrorBg, fg: token.colorErrorText, border: token.colorErrorBorder };
    case 'word':
      return { bg: token.colorInfoBg, fg: token.colorInfoText, border: token.colorInfoBorder };
    case 'spreadsheet':
      // Green to match the conventional Office Excel swatch — uses the success
      // token so dark/light themes auto-flip.
      return { bg: token.colorSuccessBg, fg: token.colorSuccessText, border: token.colorSuccessBorder };
    case 'presentation':
      // Orange/amber to match the conventional Office PowerPoint swatch.
      // colorWarning* is the closest Ant Design token and doesn't clash with
      // Excel's green or Word's blue.
      return { bg: token.colorWarningBg, fg: token.colorWarningText, border: token.colorWarningBorder };
    case 'text':
      return { bg: token.colorFillTertiary, fg: token.colorTextSecondary, border: token.colorBorderSecondary };
    case 'font':
      // Brand material, read by tools rather than shown: the neutral fill,
      // so a font chip never competes with the documents beside it.
      return { bg: token.colorFillTertiary, fg: token.colorTextSecondary, border: token.colorBorderSecondary };
    case 'audio':
      return { bg: token.colorWarningBg, fg: token.colorWarningText, border: token.colorWarningBorder };
    case 'video':
      return { bg: token.colorWarningBg, fg: token.colorWarningText, border: token.colorWarningBorder };
    default:
      return { bg: token.colorFillTertiary, fg: token.colorTextSecondary, border: token.colorBorderSecondary };
  }
}

/* ------------------------------------------------------------------------ */
/* SVG icon set — no emojis                                                  */
/* ------------------------------------------------------------------------ */

function DocumentIcon({ color = 'currentColor', size = 22 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M14 2v6h6"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function PaperclipIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* Image thumbnail loader                                                    */
/* ------------------------------------------------------------------------ */

function useImageThumbnail(source: File | string | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!source) {
      setUrl(undefined);
      return;
    }

    // Local File object (preview mode in ChatInput before send) — make a
    // blob URL we own and revoke on cleanup. No backend round-trip.
    if (typeof source !== 'string') {
      const blobUrl = URL.createObjectURL(source);
      setUrl(blobUrl);
      return () => URL.revokeObjectURL(blobUrl);
    }

    // String source — could be:
    //   (a) a relative API path like `/files/preview-by-id/<id>` that needs
    //       the authenticated Axios client to attach the JWT bearer (a
    //       bare `<img src="/files/...">` can't pass headers and the
    //       endpoint would 401),
    //   (b) an external/absolute URL (legacy) that the browser can load
    //       directly.
    // For (a), fetch as blob via the api client, then create an object URL
    // and use that as the <img> src. For (b), pass through unchanged.
    const isApiPath = source.startsWith('/files/') || source.startsWith('/api/');
    if (!isApiPath) {
      setUrl(source);
      return;
    }

    let revoked = false;
    let createdBlobUrl: string | undefined;
    api.get(source, { responseType: 'blob' })
      .then((resp) => {
        if (revoked) return;
        createdBlobUrl = URL.createObjectURL(resp.data);
        setUrl(createdBlobUrl);
      })
      .catch(() => {
        if (revoked) return;
        setUrl(undefined);  // falls back to the document icon
      });
    return () => {
      revoked = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [source]);

  return url;
}

/* ------------------------------------------------------------------------ */
/* Card                                                                      */
/* ------------------------------------------------------------------------ */

export type AttachmentStatus = 'pending' | 'valid' | 'invalid';

interface FileAttachmentCardProps {
  /** Either a local File (preview mode) or a stored attachment's file_id. */
  file?: File;
  /** For attached mode: server-side identifiers for previewing. */
  fileId?: string;
  /** Display name and size — required in both modes. */
  fileName: string;
  fileSize?: number;
  /** MIME type, optional — used as a fallback when extension is ambiguous. */
  fileType?: string;
  /** Render mode. */
  mode: 'preview' | 'attached';
  /** Preview-mode only. */
  status?: AttachmentStatus;
  errorText?: string;
  onRemove?: () => void;
  /** Attached-mode only. */
  onClick?: () => void;
  /** Provenance — set for files that came from Google Drive. Renders the Drive
   *  badge on the icon and, once sent, an "Open in Drive" action. */
  source?: AttachmentSource | null;
}

export function FileAttachmentCard({
  file,
  fileId,
  fileName,
  fileSize,
  fileType,
  mode,
  status = 'valid',
  errorText,
  onRemove,
  onClick,
  source,
}: FileAttachmentCardProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const isDrive = source?.provider === 'google_drive';
  const driveLink = isDrive ? safeHttpUrl(source?.web_view_link) : null;

  const kind = useMemo(() => getFileKind(fileName, fileType), [fileName, fileType]);
  const accent = useKindAccent(kind);
  const isImage = kind === 'image';
  // Image thumbnail source: the local File (preview mode, before send), else
  // the stored file by its id — the JWT-protected `preview-by-id` endpoint,
  // which `useImageThumbnail` fetches through the auth'd Axios client. The
  // callers used to pass the attachment's `file_path` as a URL: a STORAGE key
  // (`tenants/<id>/<name>.png`) the browser resolved against the page, and
  // every uploaded image in a past message showed broken (2026-10-19).
  const thumbnailSource: File | string | undefined = isImage
    ? file ?? (fileId ? `/files/preview-by-id/${fileId}` : undefined)
    : undefined;
  const thumbnailUrl = useImageThumbnail(thumbnailSource);

  // Isolated: inside a Hebrew line "38.4 KB" read as "KB 38.4".
  const sizeLabel = fileSize != null ? bidiIsolate(formatBytes(fileSize)) : undefined;
  const isInvalid = status === 'invalid';
  const isPending = status === 'pending';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mode === 'attached' && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className={[
        'attachment-card',
        `attachment-card--${mode}`,
        isImage && 'attachment-card--image',
        isInvalid && 'attachment-card--invalid',
        isPending && 'attachment-card--pending',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        background: isImage ? token.colorFillTertiary : token.colorBgContainer,
        borderColor: isInvalid ? token.colorErrorBorder : token.colorBorderSecondary,
      }}
      onClick={mode === 'attached' && !isInvalid ? onClick : undefined}
      onKeyDown={mode === 'attached' ? handleKeyDown : undefined}
      role={mode === 'attached' ? 'button' : undefined}
      tabIndex={mode === 'attached' ? 0 : undefined}
      title={isInvalid && errorText ? errorText : fileName}
    >
      {/* Visual: thumbnail (images) OR colored document icon (docs) */}
      {isImage && thumbnailUrl ? (
        <div className="attachment-card__thumbnail">
          <img src={thumbnailUrl} alt={fileName} loading="lazy" />
          {isDrive && <span className="attachment-card__source-badge" title={t('chat.drive.sourceLabel')}><GoogleDriveGlyph size={11} /></span>}
        </div>
      ) : (
        <div
          className="attachment-card__icon"
          style={{
            background: accent.bg,
            color: accent.fg,
            border: `1px solid ${accent.border}`,
          }}
        >
          <DocumentIcon color={accent.fg} />
          <span className="attachment-card__kind">{getKindLabel(kind, fileName)}</span>
          {isDrive && <span className="attachment-card__source-badge" title={t('chat.drive.sourceLabel')}><GoogleDriveGlyph size={11} /></span>}
        </div>
      )}

      {/* Filename + size (or error) */}
      <div className="attachment-card__meta">
        <div
          className="attachment-card__name"
          style={{ color: token.colorText }}
        >
          {fileName}
        </div>
        <div
          className="attachment-card__sub"
          style={{
            color: isInvalid ? token.colorErrorText : token.colorTextSecondary,
          }}
        >
          {isInvalid
            ? errorText || t('common.error', { defaultValue: 'Error' })
            : isPending
              ? t('chat.thinking.working', { defaultValue: 'Working on it…' })
              : isDrive
                ? [t('chat.drive.sourceLabel'), sizeLabel].filter(Boolean).join(' · ')
                : sizeLabel}
        </div>
      </div>

      {/* Preview mode: remove button. Attached mode: subtle paperclip hint. */}
      {mode === 'preview' && onRemove && (
        <button
          type="button"
          className="attachment-card__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title={t('common.remove')}
          aria-label={t('common.remove')}
          style={{ color: token.colorTextSecondary }}
        >
          <XIcon />
        </button>
      )}
      {mode === 'attached' && driveLink ? (
        <a
          className="attachment-card__open"
          href={driveLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title={t('chat.drive.openInDrive')}
          aria-label={t('chat.drive.openInDrive')}
          style={{ color: token.colorTextSecondary }}
        >
          <ExternalLinkIcon size={14} />
        </a>
      ) : mode === 'attached' && (
        <span
          className="attachment-card__hint"
          style={{ color: token.colorTextTertiary }}
          aria-hidden="true"
        >
          <PaperclipIcon size={14} />
        </span>
      )}
    </div>
  );
}
